<template>
  <el-dialog
    :title="dm.title"
    :visible.sync="dialogVisible"
    width="800px"
    class="demand-match-dialog"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="dialog-content">
      <el-form
        ref="form"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        label-position="right"
        class="consult-form"
      >
        <!-- 姓名和手机号一行 -->
        <div class="form-row">
          <el-form-item :label="dm.labelName" prop="name" required class="form-item-half">
            <el-input
              v-model="formData.name"
              :placeholder="dm.placeholderName"
              maxlength="50"
            />
          </el-form-item>

          <el-form-item :label="dm.labelMobile" prop="mobile" required class="form-item-half">
            <el-input
              v-model="formData.mobile"
              :placeholder="dm.placeholderMobile"
              maxlength="11"
            />
          </el-form-item>
        </div>

        <!-- 需求名称 -->
        <el-form-item :label="dm.labelDemandName" prop="demand" required class="demand-select-row">
          <div class="demand-select-wrapper">
            <el-input
              v-model="formData.demand"
              :placeholder="dm.placeholderDemandName"
              class="demand-input"
            />
            <div class="demand-action-group">
              <el-button
                type="text"
                class="action-btn"
                @click="openDemandSelector"
              >
                {{ dm.btnSelectDemand }}
              </el-button>
              <el-button
                type="text"
                class="action-btn"
                @click="handlePublish"
              >
                {{ dm.btnPublishDemand }}
              </el-button>
            </div>
          </div>
        </el-form-item>

        <!-- 领域 -->
        <el-form-item :label="dm.labelField" prop="field" required>
          <el-radio-group v-model="formData.field">
            <el-radio v-for="opt in fieldRadioOptions" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 需求描述 -->
        <el-form-item :label="dm.labelDescription" prop="description" required>
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="4"
            :placeholder="dm.placeholderInput"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <!-- 截止时间 -->
        <el-form-item :label="dm.labelDeadline" prop="deadline" required>
          <el-date-picker
            v-model="formData.deadline"
            type="date"
            :placeholder="dm.placeholderSelectDate"
            style="width: 100%"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
          />
        </el-form-item>

        <!-- 期望服务形式 -->
        <el-form-item :label="dm.labelServiceForm" prop="serviceForm" required class="service-form-item">
          <el-radio-group v-model="formData.serviceForm">
            <el-radio v-for="opt in serviceFormOptions" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 附件（选填） -->
        <el-form-item :label="dm.labelAttachment">
          <div class="upload-wrapper">
            <el-upload
              ref="upload"
              :action="uploadAction"
              :headers="uploadHeaders"
              :file-list="fileList"
              :on-success="handleUploadSuccess"
              :on-remove="handleRemove"
              :before-upload="beforeUpload"
              :limit="10"
              multiple
            >
              <el-button size="small" class="upload-btn">
                <i class="el-icon-upload2"></i> {{ dm.uploadFile }}
              </el-button>
            </el-upload>
            <span class="upload-hint">
              {{ dm.uploadHint }}
            </span>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 需求选择器弹窗 -->
    <el-dialog
      :visible.sync="selectorVisible"
      :title="dm.selectorTitle"
      width="600px"
      append-to-body
      :close-on-click-modal="false"
      class="demand-selector-dialog"
    >
      <div class="selector-search-row">
        <el-input
          v-model="selectorKeyword"
          :placeholder="dm.placeholderDemandSearch"
          @keyup.enter.native="searchDemands"
        />
        <el-button class="selector-search-btn" @click="searchDemands">
          {{ dm.btnSearch }}
        </el-button>
      </div>
      <div class="selector-list">
        <div
          v-for="item in selectorList"
          :key="item.id"
          class="selector-item"
          @click="selectDemand(item)"
        >
          <div class="selector-item-name">{{ item.requirementName }}</div>
          <div class="selector-item-meta">
            <span v-if="item.contactPerson">{{ dm.applicantPrefix }}{{ item.contactPerson }}</span>
            <span v-if="item.deadline">{{ dm.deadlinePrefix }}{{ item.deadline }}</span>
          </div>
        </div>
        <div v-if="selectorSearched && selectorList.length === 0" class="selector-empty">
          {{ dm.selectorEmpty }}
        </div>
      </div>
    </el-dialog>

    <div slot="footer" class="dialog-footer">
      <el-button class="return-btn" @click="handleClose">{{ dm.btnBack }}</el-button>
      <el-button class="submit-btn" @click="handleSubmit">{{ dm.btnSubmit }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import baseUrl from '@/utils/baseUrl'
import request from '@/utils/request'
import { trackConsultSubmit, trackMatchSelf } from '@/utils/behaviorTrack'
import supplyDemandApi from '@/api/supplyDemand'
import { getText } from '@/utils/i18nText'
import {
  DEMAND_MATCH_DEFAULT_FIELD,
  DEMAND_MATCH_DEFAULT_SERVICE
} from '@/config/globalTextConfig'

export default {
  name: 'DemandMatchDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 模块类型：tec(成果) / demand(需求) / instrument(仪器) / product(商品)
    // 用于咨询提交时 payload.type
    moduleType: {
      type: String,
      default: 'demand'
    },
    demandTitle: {
      type: String,
      default: ''
    },
    demandId: {
      type: [String, Number],
      default: ''
    },
    // 咨询来源类型：demand（默认：需求）、talent（人才咨询）
    sourceType: {
      type: String,
      default: 'demand'
    },
    // 当为人才咨询时，传入的人才项或至少包含 id、title/name
    project: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      formData: {
        name: '',
        mobile: '',
        demand: '',
        demandId: '',
        field: DEMAND_MATCH_DEFAULT_FIELD,
        description: '',
        deadline: '',
        serviceForm: DEMAND_MATCH_DEFAULT_SERVICE,
        attachments: []
      },
      selectedRequirement: null,
      fileList: [],
      uploadAction: `${baseUrl.matcherAuth}/file/upload?isLogo=0`,
      requirementSearchLoading: false,
      // 字典：领域(yField)
      fieldDictOptions: [],
      // 需求选择器
      selectorVisible: false,
      selectorKeyword: '',
      selectorList: [],
      selectorSearched: false
    }
  },
  created() {
    this.loadFieldDict()
  },
  computed: {
    dm() {
      const g = (k, fb) => getText(`demandMatchDialog.${k}`, fb)
      return {
        title: this.isTalentConsult ? g('titleConsult', '我要咨询') : g('titleMatch', '我要对接'),
        labelName: g('labelName', '姓名'),
        labelMobile: g('labelMobile', '手机号'),
        labelSelectDemand: g('labelSelectDemand', '选择需求'),
        labelField: g('labelField', '所属领域'),
        labelDescription: g('labelDescription', '需求描述'),
        labelDeadline: g('labelDeadline', '截止时间'),
        labelServiceForm: g('labelServiceForm', '期望服务形式'),
        labelAttachment: g('labelAttachment', '附件'),
        labelDemandName: g('labelDemandName', '需求名称'),
        placeholderDemandName: g('placeholderDemandName', '请输入需求名称'),
        btnSelectDemand: g('btnSelectDemand', '选择需求'),
        btnPublishDemand: g('btnPublishDemand', '发布需求'),
        selectorTitle: g('selectorTitle', '选择需求'),
        btnSearch: g('btnSearch', '搜索'),
        selectorEmpty: g('selectorEmpty', '暂无数据'),
        placeholderName: g('placeholderName', '请输入姓名'),
        placeholderMobile: g('placeholderMobile', '请输入手机号'),
        placeholderDemandSearch: g('placeholderDemandSearch', '请输入需求名称搜索'),
        placeholderInput: g('placeholderInput', '请输入'),
        placeholderSelectDate: g('placeholderSelectDate', '请选择'),
        applicantPrefix: g('applicantPrefix', '申报人：'),
        deadlinePrefix: g('deadlinePrefix', '截止时间：'),
        noDemandHint: g('noDemandHint', '暂无需求,去发布我的需求 >>'),
        goPublish: g('goPublish', '去发布'),
        uploadFile: g('uploadFile', '上传文件'),
        uploadHint: g(
          'uploadHint',
          '数量不限,支持JPG、JPEG、PNG、GIF、PDF格式,单个图片大小不超过20M。'
        ),
        btnBack: g('btnBack', '返回'),
        btnSubmit: g('btnSubmit', '提交'),
        errMobileRequired: g('errMobileRequired', '请输入手机号'),
        errMobileFormat: g('errMobileFormat', '请输入正确的手机号'),
        errNameRequired: g('errNameRequired', '请输入姓名'),
        errFieldRequired: g('errFieldRequired', '请选择领域'),
        errDemandRequired: g('errDemandRequired', '请选择需求'),
        errDemandPickFromList: g('errDemandPickFromList', '请从下拉列表中选择需求'),
        errDescriptionRequired: g('errDescriptionRequired', '请输入需求描述'),
        errDeadlineRequired: g('errDeadlineRequired', '请选择截止时间'),
        errServiceFormRequired: g('errServiceFormRequired', '请选择期望服务形式'),
        loginCheckFailed: g('loginCheckFailed', '检查登录状态失败:'),
        loginConfirmMessage: g(
          'loginConfirmMessage',
          '您还未登录，需要登录后才可以使用该功能，是否前往登录？'
        ),
        loginConfirmTitle: g('loginConfirmTitle', '提示'),
        loginConfirmOk: g('loginConfirmOk', '前往登录'),
        loginConfirmCancel: g('loginConfirmCancel', '取消'),
        searchDemandFailed: g('searchDemandFailed', '搜索需求失败:'),
        submitSuccess: g('submitSuccess', '已提交'),
        submitFailed: g('submitFailed', '提交失败，请稍后重试'),
        uploadTypeError: g('uploadTypeError', '上传文件只能是 JPG、JPEG、PNG、GIF、PDF 格式!'),
        uploadSizeError: g('uploadSizeError', '上传文件大小不能超过 20MB!')
      }
    },
    fieldRadioOptions() {
      // 使用数据字典(yField)；为空则不显示选项
      return Array.isArray(this.fieldDictOptions) ? this.fieldDictOptions : []
    },
    serviceFormOptions() {
      // 使用 code 作为 value，label 从全局文案配置读取，避免组件内硬编码中文枚举
      const keys = ['online', 'onsite', 'wechatVideo', 'phone']
      return keys.map(key => ({
        value: key,
        label: getText(`demandMatchDialog.serviceForm.${key}`)
      }))
    },
    uploadHeaders() {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token') || ''
      return token ? { 'X-CSRF-TOKEN': token } : {}
    },
    formRules() {
      const dm = this.dm
      const validateMobile = (rule, value, callback) => {
        if (!value) callback(new Error(dm.errMobileRequired))
        else if (!/^1[3-9]\d{9}$/.test(value)) callback(new Error(dm.errMobileFormat))
        else callback()
      }
      return {
        name: [{ required: true, message: dm.errNameRequired, trigger: 'blur' }],
        mobile: [{ required: true, validator: validateMobile, trigger: 'blur' }],
        field: [{ required: true, message: dm.errFieldRequired, trigger: 'change' }],
        demand: [{ required: true, message: dm.errDemandRequired, trigger: 'blur' }],
        description: [{ required: true, message: dm.errDescriptionRequired, trigger: 'blur' }],
        deadline: [{ required: true, message: dm.errDeadlineRequired, trigger: 'change' }],
        serviceForm: [{ required: true, message: dm.errServiceFormRequired, trigger: 'change' }]
      }
    },
    // 是否为人才咨询
    isTalentConsult() {
      return this.sourceType === 'talent'
    },
    // AI客服入口：无人才信息，走“需求咨询”接口（与 talent 分支区分开）
    isConsultFromAi() {
      return this.sourceType === 'consult'
    },
    // 兼容兜底后的模块类型
    consultModuleType() {
      const allow = ['tec', 'demand', 'instrument', 'product']
      return allow.includes(this.moduleType) ? this.moduleType : 'demand'
    },
    // 人才ID
    expertUserId() {
      // 优先使用 project.id，其次 project.expertUserId
      return this.project?.id || this.project?.expertUserId || ''
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 打开前先做登录校验（异步）
        this.checkLogin().then(isOk => {
          if (!isOk) {
            this.dialogVisible = false
            this.$emit('update:visible', false)
            return
          }
          // 通过校验后再真正打开弹窗并初始化表单
          this.dialogVisible = true
          this.resetForm()
          if (this.demandTitle) {
            this.formData.demand = this.demandTitle
          }
          if (this.demandId) {
            this.formData.demandId = this.demandId
          }
        })
      } else {
        this.dialogVisible = false
      }
    },
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal)
    },
    demandTitle(newVal) {
      if (newVal && this.dialogVisible) {
        this.formData.demand = newVal
      }
    },
    demandId(newVal) {
      if (newVal && this.dialogVisible) {
        this.formData.demandId = newVal
      }
    }
  },
  methods: {
    // 检查登录状态（与 PageHeader 统一：调用后端接口判断）
    async checkLogin() {
      try {
        const res = await request({
          url: '/uoc/umc/user/my/q/simpleinfo',
          method: 'get'
        })
        const result = res.data || {}
        if (result.status === 0 && result.data) {
          // 已登录
          return true
        }
      } catch (error) {
        // 接口异常时按未登录处理，走弹窗提示
        // eslint-disable-next-line no-console
        console.error(this.dm.loginCheckFailed, error)
      }

      return this.$confirm(
        this.dm.loginConfirmMessage,
        this.dm.loginConfirmTitle,
        {
          confirmButtonText: this.dm.loginConfirmOk,
          cancelButtonText: this.dm.loginConfirmCancel,
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        }
      )
        .then(() => {
          const currentUrl = encodeURIComponent(window.location.href)
          window.open(`/main/#/login?redirectURL=${currentUrl}`, '_blank')
          return false
        })
        .catch(() => {
          // 取消，不做操作
          return false
        })
    },
    resetForm() {
      this.formData = {
        name: '',
        mobile: '',
        demand: '',
        demandId: '',
        field: DEMAND_MATCH_DEFAULT_FIELD,
        description: '',
        deadline: '',
        serviceForm: DEMAND_MATCH_DEFAULT_SERVICE,
        attachments: []
      }
      this.selectedRequirement = null
      this.fileList = []
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles()
      }
    },
    handleClose() {
      this.dialogVisible = false
      this.resetForm()
    },
    handlePublish() {
      this.$emit('publish')
      this.handleClose()

      // 点击“去发布”跳转到声明人我的需求页面
      // 这里使用强制跳转（而不是 $router.push），因为目标是 /main 下的非本项目 hash 路由。
      const url = '/main/?menuId=f27d85349b7a40ee9cd303a1cb86cff2#/requirement-web/declarant/my-requirement'
      window.location.href = url
    },
    // 加载领域(yField)数据字典
    async loadFieldDict() {
      try {
        const res = await supplyDemandApi.getDictData(['yField'])
        const result = res.data || {}
        if (result.status === 0) {
          // 兼容 rows / 数组 / 对象
          let rows = []
          if (Array.isArray(result.data)) rows = result.data
          else if (Array.isArray(result.data?.rows)) rows = result.data.rows
          else if (result.data && typeof result.data === 'object') rows = Object.values(result.data).flat()

          const yField = (rows || [])
            .filter(it => it && it.dictCode === 'yField')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
            .map(it => ({
              value: it.itemCode || '',
              label: it.itemValue || ''
            }))
            .filter(it => it.value && it.label)

          this.fieldDictOptions = yField

          // 保证当前选中值与字典 value 对齐；若不匹配则回退为首项
          this.formData.field = this.resolveFieldValue(this.formData.field)
        }
      } catch (e) {
        // ignore - 使用兜底项
      }
    },
    // 将任意领域原始值规范为字典 value(itemCode)
    resolveFieldValue(rawField) {
      const options = Array.isArray(this.fieldDictOptions) ? this.fieldDictOptions : []
      if (!options.length) return rawField || ''

      const raw = rawField == null ? '' : String(rawField).trim()
      if (!raw) return options[0].value

      const byValue = options.find(opt => String(opt.value) === raw)
      if (byValue) return byValue.value

      const byLabel = options.find(opt => String(opt.label) === raw)
      if (byLabel) return byLabel.value

      return options[0].value
    },
    // 搜索需求
    async queryRequirementSearch(queryString, cb) {
      if (!queryString || queryString.trim() === '') {
        cb([])
        return
      }
      
      try {
        this.requirementSearchLoading = true
        const res = await supplyDemandApi.getRequirementListByName(queryString.trim())
        const result = res.data || {}
        
        if (result.status === 0 && Array.isArray(result.data)) {
          // 格式化数据供 autocomplete 使用
          const suggestions = result.data.map(item => ({
            value: item.requirementName,
            requirementName: item.requirementName,
            id: item.id,
            field: item.field,
            deadline: item.deadline ? item.deadline.split(' ')[0] : '', // 只取日期部分
            contactPerson: item.contactPerson || '',
            contactPhone: item.contactPhone || '',
            phonePublic: item.phonePublic,
            requirementDesc: item.requirementDesc || '',
            requirementFileVOS: item.requirementFileVOS || [],
            // 保存完整数据
            rawData: item
          }))
          cb(suggestions)
        } else {
          cb([])
        }
      } catch (error) {
        console.error(this.dm.searchDemandFailed, error)
        cb([])
      } finally {
        this.requirementSearchLoading = false
      }
    },
    // 选择需求
    handleRequirementSelect(item) {
      if (item && item.rawData) {
        this.selectDemand({
          ...item.rawData,
          deadline: item.deadline
        })
      }
    },
    openDemandSelector() {
      this.selectorVisible = true
      this.selectorKeyword = ''
      this.selectorList = []
      this.selectorSearched = false
    },
    async searchDemands() {
      if (!this.selectorKeyword || this.selectorKeyword.trim() === '') {
        this.selectorList = []
        this.selectorSearched = true
        return
      }
      try {
        const res = await supplyDemandApi.getRequirementListByName(this.selectorKeyword.trim())
        const result = res.data || {}
        if (result.status === 0 && Array.isArray(result.data)) {
          this.selectorList = result.data.map(item => ({
            ...item,
            deadline: item.deadline ? item.deadline.split(' ')[0] : ''
          }))
        } else {
          this.selectorList = []
        }
        this.selectorSearched = true
      } catch (error) {
        console.error(this.dm.searchDemandFailed, error)
        this.selectorList = []
        this.selectorSearched = true
      }
    },
    selectDemand(item) {
      this.selectedRequirement = item
      this.formData.demandId = item.id
      this.formData.demand = item.requirementName

      // 用需求中的领域预填，并统一转为字典 value(itemCode)
      const pickedField =
        item.fieldId ||
        item.field ||
        item.fieldCode ||
        item.fieldLabel ||
        item.yField
      this.formData.field = this.resolveFieldValue(pickedField)

      // 如果选中的需求有描述，自动填充
      if (item.requirementDesc) {
        this.formData.description = item.requirementDesc
      }

      // 如果选中的需求有截止时间，自动填充
      if (item.deadline) {
        this.formData.deadline = item.deadline
      }

      this.selectorVisible = false

      // 触发验证
      if (this.$refs.form) {
        this.$refs.form.validateField('demand')
      }
    },
    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return
        }

        // 服务形式映射为接口所需的数值 / code
        const serviceTypeMap = {
          online: 1,
          onsite: 2,
          wechatVideo: 3,
          phone: 4
        }

        // id 使用上级页面传入的 demandId（props）
        const id = this.demandId || ''
        // demandId 使用选择的需求对应的 id
        const demandId = this.formData.demandId || (this.selectedRequirement ? this.selectedRequirement.id : '')
        // 需求名称：优先使用选择的需求名称，其次使用 props 传入的
        const finalDemandName = this.formData.demand || (this.selectedRequirement ? this.selectedRequirement.requirementName : '') || this.demandTitle || ''
        // 优先使用用户当前选择；其次用已选需求中的领域id，并统一为字典 value(itemCode)
        const finalField = this.resolveFieldValue(
          this.formData.field ||
          (this.selectedRequirement
            ? (this.selectedRequirement.fieldId ||
               this.selectedRequirement.field ||
               this.selectedRequirement.fieldCode ||
               this.selectedRequirement.fieldLabel)
            : '')
        )

        // 公共附件转换
        const commonFiles = this.fileList.map(file => ({
          fileName: file.name,
          fileId: file.fileId || file.response?.data || '',
          fileType: file.type || ''
        }))

        let url = ''
        let baseURL = ''
        let payload = null

        if (this.isTalentConsult) {
          // 人才咨询（原有逻辑）：有 expertUserId，走原接口/原参数结构
          payload = {
            isRegister: demandId ? 1 : 0,
            requirementId: demandId || '',
            requirementName: finalDemandName || '',
            requirementDesc: this.formData.description || '',
            domain: finalField || '',
            deadline: this.formData.deadline || '',
            serviceForm: String(serviceTypeMap[this.formData.serviceForm] || ''),
            name: this.formData.name,
            phone: this.formData.mobile,
            expertUserId: this.expertUserId,
            files: commonFiles
          }
          url = '/expert/expert-consultation/interface'
          baseURL = baseUrl.apiBaseAuth
        } else if (this.isConsultFromAi) {
          // AI客服入口咨询：无人才信息，走新接口/新参数结构
          payload = {
            isRegister: demandId ? 1 : 0,
            requirementId: demandId || '',
            requirementName: finalDemandName || '',
            requirementDesc: this.formData.description || '',
            domain: finalField || '',
            deadline: this.formData.deadline || '',
            serviceForm: String(serviceTypeMap[this.formData.serviceForm] || ''),
            name: this.formData.name,
            phone: this.formData.mobile,
            files: commonFiles
          }
          url = '/expert/expert-consultation/demand'
          baseURL = baseUrl.apiBaseAuth
        } else {
          // 需求、成果、仪器、商品等咨询 - 使用原有接口
          payload = {
            type: this.consultModuleType, // tec(成果)/demand(需求)/instrument(仪器)/product(商品)
            name: this.formData.name,
            mobile: this.formData.mobile,
            content: this.formData.description,
            id: id, // 使用上级页面传入的 demandId
            isRecord: demandId ? 1 : 0, // 如果选择了需求，标记为已登记
            demandId: demandId, // 使用选择的需求对应的 id
            demandName: finalDemandName,
            domainType: finalField, // 使用需求中的领域ID或表单中选择的领域
            endDate: this.formData.deadline,
            serviceType: serviceTypeMap[this.formData.serviceForm] || '',
            fileList: this.fileList.map(file => ({
              fileName: file.name,
              // 提交时使用上传接口返回的 data 作为 url（文件id）
              url: file.fileId || file.response?.data || ''
            }))
          }
          url = ''
          baseURL = baseUrl.ask
        }

        try {
          const res = await request({
            url,
            method: 'post',
            data: payload,
            baseURL,
            headers: {
              'Content-Type': 'application/json'
            }
          })

          const result = res.data || {}
          if (result.status === 0) {
            if (this.isTalentConsult) {
              trackConsultSubmit({
                formContent: this.formData.description,
                mobile: this.formData.mobile,
                relatedResultId: this.expertUserId || demandId
              })
            } else {
              trackMatchSelf({
                matchType: this.consultModuleType,
                targetType: this.consultModuleType,
                targetId: String(id || demandId || '')
              })
              trackConsultSubmit({
                formContent: this.formData.description,
                mobile: this.formData.mobile,
                relatedResultId: String(id || demandId || '')
              })
            }
            this.$message.success(result.msg || this.dm.submitSuccess)
            this.$emit('submit', result.data)
            this.handleClose()
          } else {
            this.$message.error(result.msg || this.dm.submitFailed)
          }
        } catch (error) {
          this.$message.error(this.dm.submitFailed)
        }
      })
    },
    handleUploadSuccess(response, file, fileList) {
      const result = response || {}
      if (Number(result.status) === 0 && result.data) {
        file.fileId = String(result.data)
      } else {
        this.$message.error(result.msg || this.dm.submitFailed)
        const uploadRef = this.$refs.upload
        if (uploadRef && typeof uploadRef.handleRemove === 'function') {
          uploadRef.handleRemove(file)
        }
        return
      }
      this.fileList = fileList
    },
    handleRemove(file, fileList) {
      this.fileList = fileList
    },
    beforeUpload(file) {
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type)
      const isLt20M = file.size / 1024 / 1024 < 20

      if (!isValidType) {
        this.$message.error(this.dm.uploadTypeError)
        return false
      }
      if (!isLt20M) {
        this.$message.error(this.dm.uploadSizeError)
        return false
      }
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.demand-match-dialog {
  ::v-deep .el-dialog {
    border-radius: 8px;
    overflow: hidden;
  }
  
  ::v-deep .el-dialog__header {
    background: #f5f3e8;
    padding: 12px 24px;
    border-bottom: 1px solid #f0f0f0;
    position: relative;

    .el-dialog__title {
      font-family: Source Han Sans CN;
      font-size: 14px;
      font-weight: 600;
      color: #333333;
      line-height: 1.5;
    }

    .el-dialog__headerbtn {
      top: 12px;
      right: 24px;
      width: 20px;
      height: 20px;

      .el-dialog__close {
        color: #a0903c;
        font-size: 18px;
        font-weight: 400;
        transition: color 0.3s;

        &:hover {
          color: #8b7a2f;
        }
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 24px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .dialog-content {
    .consult-form {
      ::v-deep .el-form-item {
        margin-bottom: 20px;

        .el-form-item__label {
          font-family: Source Han Sans CN;
          font-size: 12px;
          font-weight: 400;
          color: #333333;
          line-height: 40px;
          text-align: right;
          padding-right: 12px;

          &::before {
            content: '*';
            color: #ff4d4f;
            margin-right: 4px;
          }
        }

        &:not(.is-required) .el-form-item__label::before {
          display: none;
        }
      }

      ::v-deep .el-input__inner {
        height: 40px;
        line-height: 40px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        font-size: 13px;
        color: #333333;

        &::placeholder {
          color: #999999;
        }

        &:focus {
          border-color: #a0903c;
        }
      }

      ::v-deep .el-textarea__inner {
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        font-size: 13px;
        color: #333333;
        padding: 10px 15px;

        &::placeholder {
          color: #999999;
        }

        &:focus {
          border-color: #a0903c;
        }
      }

      ::v-deep .el-select {
        .el-input__inner {
          cursor: pointer;
        }
      }

      // 期望服务形式表单项
      .service-form-item {
        ::v-deep .el-form-item {
          display: flex;
          align-items: center;
        }

        ::v-deep .el-form-item__label {
          line-height: 1.5;
          padding-top: 0;
          padding-bottom: 0;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          text-align: right;
        }

        ::v-deep .el-form-item__content {
          display: flex;
          align-items: center;
          line-height: 1.5;
          flex: 1;
        }
      }

      ::v-deep .el-radio-group {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        align-items: center;

        .el-radio {
          margin-right: 0;
          margin-bottom: 0;
          white-space: nowrap;

          .el-radio__label {
            font-family: Source Han Sans CN;
            font-size: 13px;
            color: #999999;
            padding-left: 8px;
            transition: color 0.3s;
          }

          // 未选中状态
          .el-radio__input .el-radio__inner {
            border-color: #dcdfe6;
            background-color: #ffffff;
          }

          // 选中状态
          .el-radio__input.is-checked .el-radio__inner {
            background-color: #a0903c;
            border-color: #a0903c;
          }

          .el-radio__input.is-checked + .el-radio__label {
            color: #a0903c;
          }

          // 悬停状态
          &:hover {
            .el-radio__label {
              color: #666666;
            }

            .el-radio__input .el-radio__inner {
              border-color: #a0903c;
            }
          }
        }
      }

      ::v-deep .el-date-editor {
        width: 100%;

        .el-input__inner {
          cursor: pointer;
        }
      }

      // 表单行布局
      .form-row {
        display: flex;
        gap: 16px;
        margin-bottom: 0;

        ::v-deep .form-item-half {
          flex: 1;
          margin-bottom: 20px;
        }
      }

      // 选择需求行
      .demand-select-row {
        margin-bottom: 20px;

        .demand-select-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;

          .demand-input {
            flex: 1;
            min-width: 0;
            width: 100%;
            
            ::v-deep .el-input__inner {
              width: 100%;
            }
          }

          .demand-action-group {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;
            white-space: nowrap;

            .action-btn {
              padding: 0;
              color: #a0903c;
              font-size: 12px;
              white-space: nowrap;

              &:hover {
                color: #8b7a2f;
              }
            }
          }
        }
      }

      .upload-wrapper {
        display: flex;
        align-items: center;
        gap: 12px;

        ::v-deep .el-upload {
          .upload-btn {
            background-color: #f5f3e8;
            border-color: #e8e0c8;
            color: #a0903c;
            font-family: Source Han Sans CN;
            font-size: 13px;
            padding: 9px 20px;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            gap: 6px;

            i {
              font-size: 16px;
              color: #a0903c;
            }

            &:hover {
              background-color: #ede8d4;
              border-color: #d4c9a8;
              color: #8b7a2f;

              i {
                color: #8b7a2f;
              }
            }
          }
        }

        .upload-hint {
          font-family: Source Han Sans CN;
          font-size: 12px;
          color: #999999;
          line-height: 1.5;
          flex: 1;
          min-width: 0;
        }
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding: 20px 24px;
    border-top: 1px solid #f0f0f0;

    .return-btn {
      background-color: #f5f3e8;
      border-color: #e8e0c8;
      color: #a0903c;
      font-family: Source Han Sans CN;
      font-size: 14px;
      padding: 10px 40px;
      border-radius: 4px;

      &:hover {
        background-color: #ede8d4;
        border-color: #d4c9a8;
        color: #8b7a2f;
      }
    }

    .submit-btn {
      background-color: #a0903c;
      border-color: #a0903c;
      color: #ffffff;
      font-family: Source Han Sans CN;
      font-size: 14px;
      padding: 10px 40px;
      border-radius: 4px;

      &:hover {
        background-color: #8b7a2f;
        border-color: #8b7a2f;
      }
    }
  }
}

// 需求选择器弹窗
.demand-selector-dialog {
  ::v-deep .el-dialog {
    border-radius: 8px;
    overflow: hidden;
  }

  ::v-deep .el-dialog__header {
    background: #f5f3e8;
    padding: 12px 24px;
    border-bottom: 1px solid #f0f0f0;

    .el-dialog__title {
      font-family: Source Han Sans CN;
      font-size: 14px;
      font-weight: 600;
      color: #333333;
      line-height: 1.5;
    }

    .el-dialog__headerbtn {
      top: 12px;
      right: 24px;
      width: 20px;
      height: 20px;

      .el-dialog__close {
        color: #a0903c;
        font-size: 18px;
        font-weight: 400;
        transition: color 0.3s;

        &:hover {
          color: #8b7a2f;
        }
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 20px 24px;
  }

  .selector-search-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;

    .el-input {
      flex: 1;
    }

    .selector-search-btn {
      background-color: #a0903c;
      border-color: #a0903c;
      color: #ffffff;
      font-family: Source Han Sans CN;
      font-size: 13px;
      padding: 9px 20px;
      border-radius: 4px;

      &:hover {
        background-color: #8b7a2f;
        border-color: #8b7a2f;
      }
    }
  }

  .selector-list {
    max-height: 400px;
    overflow-y: auto;

    .selector-item {
      padding: 12px;
      border-bottom: 1px solid #f0f0f0;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f5f7fa;
      }

      .selector-item-name {
        font-family: Source Han Sans CN;
        font-size: 13px;
        color: #333333;
        font-weight: 500;
        margin-bottom: 4px;
      }

      .selector-item-meta {
        font-family: Source Han Sans CN;
        font-size: 12px;
        color: #999999;
        display: flex;
        gap: 16px;

        span {
          white-space: nowrap;
        }
      }
    }

    .selector-empty {
      text-align: center;
      padding: 40px 0;
      color: #999999;
      font-size: 13px;
    }
  }
}
</style>
